import React, { useRef, useState } from "react";
import { Upload, Loader2, CheckCircle2, FileText, X } from "lucide-react";
import { uploadFile, fileUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Uploads a single file to object storage and reports the stored path via onUploaded(url).
export function FileUpload({ label, accept = "image/*,application/pdf", value, onUploaded, testid, compact }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");

  const pick = () => inputRef.current?.click();

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await uploadFile(file);
      setName(file.name);
      onUploaded(res.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const isImage = value && !value.toLowerCase().endsWith(".pdf");

  return (
    <div>
      {label && <label className="text-xs text-slate-400 mb-1.5 block">{label}</label>}
      <input ref={inputRef} type="file" accept={accept} onChange={onChange} className="hidden" data-testid={testid ? `${testid}-input` : undefined} />
      {value ? (
        <div className="flex items-center gap-3 bg-[#0B0E1B] border border-emerald-400/30 rounded-xl px-3 py-2.5">
          {isImage ? (
            <img src={fileUrl(value)} alt="" className="w-10 h-10 rounded-lg object-cover" />
          ) : (
            <FileText className="w-5 h-5 text-emerald-400" />
          )}
          <span className="text-xs text-emerald-300 flex-1 truncate flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> {name || "Uploaded"}
          </span>
          <button type="button" onClick={() => onUploaded("")} className="text-slate-400 hover:text-[#FF5E7E]">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          data-testid={testid}
          className={`w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-[#2A3350] bg-[#0B0E1B] text-slate-300 hover:border-cyan-400/50 hover:text-cyan-300 transition-colors ${compact ? "px-3 py-2.5 text-xs" : "px-4 py-4 text-sm"}`}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {busy ? "Uploading…" : "Choose file"}
        </button>
      )}
    </div>
  );
}

export default FileUpload;
